import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';

import { select, Store } from '@ngrx/store';

import { isNil, isUndefined } from 'lodash-es';
import { map, Observable, of } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { MainTabBasePathEnum } from '../../../../../enums/main-tabs';
import * as fromPropertyProfileActions from '../../../../../store/actions/property-profile.actions';
import * as fromPropertyProfileSelectors from '../../../../../store/selectors/property-profile.selectors';
import { GenericComponent } from '../../../../components/generic/generic.component';
import { AddressPipe } from '../../../../pipes/address.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-profile-report-preview',
  standalone: false,
  templateUrl: './preview.page.html'
})
export class ReportPreviewPageComponent extends GenericComponent {
  private domSanitizer = inject(DomSanitizer);
  private activatedRoute = inject(ActivatedRoute);
  private addressPipe = inject(AddressPipe);
  private store = inject(Store);

  profileId: string | null;
  reportHtml$: Observable<SafeHtml | null>;

  defaultBackHref: string | undefined;

  constructor() {
    super();

    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'] ?? MainTabBasePathEnum.PropertySearch;
    this.profileId = this.activatedRoute.snapshot.paramMap.get('id');

    this.reportHtml$ = of(this.profileId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) =>
        this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileExportPreviewHtml(id)))
      ),
      filter((reportHtml) => !isUndefined(reportHtml)),
      map((reportHtml) => {
        if (reportHtml) {
          const parser = new DOMParser();
          const document = parser.parseFromString(reportHtml, 'text/html');

          return this.domSanitizer.bypassSecurityTrustHtml(document.body.innerHTML);
        }

        return reportHtml;
      })
    );
  }

  ionViewWillEnter() {
    if (this.profileId) {
      this.store.dispatch(
        fromPropertyProfileActions.loadReportHtml({
          payload: {
            reportId: this.profileId
          }
        })
      );
    }
  }

  exportReport() {
    this.addUniqueSubscription(
      'propertyReportExport',
      of(this.profileId)
        .pipe(
          filter((id) => !isNil(id)),
          concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfile(id)))),
          filter((propertyProfile) => !isUndefined(propertyProfile))
        )
        .subscribe((propertyProfile) => {
          this.store.dispatch(
            fromPropertyProfileActions.exportReportPdf({
              payload: { address: this.addressPipe.transform(propertyProfile), reportId: this.profileId || '' }
            })
          );
        })
    );
  }
}
